// @ts-nocheck
import React, { useState, useCallback } from 'react';
import Barcode from 'react-barcode';
import {
  FiPackage,
  FiCheckCircle,
  FiXCircle,
  FiZap,
  FiImage,
  FiFileText,
} from 'react-icons/fi';
import { getLabelIcon } from '@/components/atoms/IconMap';
import { MdQrCode2, MdHistory } from 'react-icons/md';
import { IoMdAddCircle } from 'react-icons/io';
import { AiFillEdit, AiFillDelete } from 'react-icons/ai';
import { TbRestore } from 'react-icons/tb';
import {
  Popover,
  PopoverHandler,
  PopoverContent,
} from '@material-tailwind/react';
import { useSelector } from 'react-redux';
import { getSmartCoolerProductsState } from '@/store/selectors/smartCoolerProducts.selector';
import { getDateTimeAgo, getDateTime } from '@/utils/dateTime';
import { getUserDataByAuth0Id } from '@/services/smartCoolerProducts';
import {
  ESmartCoolerProductTransactionAction,
  getUserDisplayName,
} from '@/pages/SmartCoolerProducts/constant';
import brokenImage from '@/assets/images/No-Image-Available.png';
import type { ISmartCoolerProduct } from '@/services/smartCoolerProducts';
import _ from 'lodash';
import {
  NoDataPlate,
  SpinnerBlock,
  CopyButton,
} from '@/components/organism/TableExpand/primitives';
import { AppButton } from '@/components/atoms/AppButton';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import {
  APP_BG,
  APP_SEMANTIC,
  APP_BORDER,
  APP_TEXT,
} from '@/constant/theme/colors.constant';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { StatCell } from '@/components/organism/Cards/StatCell';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { GradientDivider } from '@/components/atoms/GradientDivider';
import SegmentedControl from '@/components/elements/SegmentedControl';
import type { PillConfig } from '@/components/organism/StatusPills/PillCell';
import { Text } from '@/components/atoms/AppText';
import { tid } from '@/utils/testId';
import { SMART_COOLER_PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/smartCoolerProducts.testIds';

const BarcodeCard = ({ value }: { value: string }) => {
  return (
    <div
      className={`flex flex-col rounded-2xl overflow-hidden border ${APP_BORDER.soft} ${APP_BG.canvas} shadow-sm hover:shadow-md transition-shadow duration-200`}
    >
      <GradientDivider />
      <div
        className={`flex items-center justify-center py-5 px-4 ${APP_BG.surface}`}
      >
        <div
          className={`bg-white rounded-xl px-4 py-3 shadow-inner border ${APP_BORDER.soft}`}
        >
          <Barcode
            value={value}
            width={1.6}
            height={56}
            fontSize={0}
            margin={0}
            displayValue={false}
            background="#ffffff"
          />
        </div>
      </div>
      <div
        className={`flex items-center gap-2 px-4 py-3 border-t ${APP_BORDER.soft}`}
      >
        <MdQrCode2 className="h-4 w-4 text-primary flex-shrink-0" />
        <Text
          as="span"
          size="base"
          weight="semibold"
          color="soft"
          className="flex-1 font-mono truncate tracking-wide"
        >
          {value}
        </Text>
        <CopyButton value={value} />
      </div>
    </div>
  );
};

const DetailsTab = ({
  row,
  detail,
}: {
  row: ISmartCoolerProduct;
  detail: any;
}) => {
  const r = row as any;
  const barcodes: string[] = detail?.barcodes ?? r?.barcodes ?? [];
  const infoRows = [
    {
      label: 'Linted ID',
      icon: getLabelIcon('Linted ID'),
      value: r?.lintedSmartCoolerProductId,
    },
    {
      label: 'Product ID',
      icon: getLabelIcon('Product ID'),
      value: r?.productId,
    },
    { label: 'Brand', icon: getLabelIcon('Brand'), value: r?.brand },
    { label: 'Category', icon: getLabelIcon('Category'), value: r?.category },
    { label: 'Spec', icon: getLabelIcon('Spec'), value: r?.spec },
  ].filter(({ value }) => value != null && value !== '');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_1fr] gap-4 items-stretch">
      {/* Image card */}
      <SectionCard
        testId={tid(TID.EXPANDED_IMAGE)}
        title="Image"
        icon={<FiImage className="h-3.5 w-3.5" />}
      >
        <div className="flex flex-col p-4 gap-3">
          {/* Image stage — white bg, subtle border, ambient glow */}
          <div className="relative w-full rounded-2xl overflow-hidden bg-white border border-gray-100 dark:border-white/[0.07]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_50%,rgba(var(--color-primary-rgb,99,102,241),0.05)_0%,transparent_100%)] pointer-events-none" />
            <img
              src={r?.productImage || brokenImage}
              alt="product"
              className="relative w-full h-[200px] object-contain p-4 transition-transform duration-300 hover:scale-[1.04]"
              onError={(e: any) => {
                e.target.src = brokenImage;
              }}
            />
          </div>

          {/* Product name chip */}
          {r?.shortProductName && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${APP_BORDER.soft} ${APP_BG.surface}`}>
              <FiPackage className="h-3.5 w-3.5 text-primary flex-shrink-0 opacity-60" />
              <Text
                as="span"
                size="sm"
                weight="semibold"
                color="soft"
                className="flex-1 truncate leading-snug"
              >
                {r.shortProductName}
              </Text>
            </div>
          )}

          {/* Deleted badge */}
          {r?.deletedOn && (
            <div className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl border ${APP_SEMANTIC.danger.badge}`}>
              <FiXCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <Text as="span" size="sm" weight="bold" color="danger" className="leading-none">
                Deleted
              </Text>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Barcodes */}
      <SectionCard
        testId={tid(TID.EXPANDED_BARCODES)}
        title="Barcodes"
        icon={<MdQrCode2 className="h-3.5 w-3.5" />}
        badge={barcodes.length}
      >
        {barcodes.length === 0 ? (
          <NoDataPlate
            message={ORGANISM_PROPS.SMART_COOLER_EXPANDED.NO_BARCODES}
          />
        ) : (
          <div className="p-4 grid grid-cols-2 gap-3">
            {barcodes.map((bc, i) => (
              <BarcodeCard key={i} value={bc} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Product Info */}
      {infoRows.length > 0 && (
        <SectionCard
          testId={tid(TID.EXPANDED_PRODUCT_INFO)}
          title="Product Info"
          icon={<FiPackage className="h-3.5 w-3.5" />}
        >
          <div className={`divide-y ${APP_BORDER.divideSoft}`}>
            {infoRows.map(({ label, icon, value }) => (
              <StatCell
                key={label}
                label={label}
                labelIcon={icon}
                value={value}
                mono
              />
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

/* ─── History tab ────────────────────────────────────────────────────────────── */

const ACTION_STYLES: Record<
  string,
  { cls: string; dot: string; icon: React.ReactNode }
> = {
  [ESmartCoolerProductTransactionAction.CREATE]: {
    cls: APP_SEMANTIC.success.badge,
    dot: APP_SEMANTIC.success.dot,
    icon: <IoMdAddCircle className="h-4 w-4" />,
  },
  [ESmartCoolerProductTransactionAction.UPDATE]: {
    cls: APP_SEMANTIC.blue.badge,
    dot: APP_SEMANTIC.blue.dot,
    icon: <AiFillEdit className="h-4 w-4" />,
  },
  [ESmartCoolerProductTransactionAction.DELETE]: {
    cls: APP_SEMANTIC.danger.badge,
    dot: APP_SEMANTIC.danger.dot,
    icon: <AiFillDelete className="h-4 w-4" />,
  },
  [ESmartCoolerProductTransactionAction.RESTORE]: {
    cls: APP_SEMANTIC.orange.badge,
    dot: APP_SEMANTIC.orange.dot,
    icon: <TbRestore className="h-4 w-4" />,
  },
};
const DEFAULT_STYLE = {
  cls: APP_SEMANTIC.neutral.badge,
  dot: APP_SEMANTIC.neutral.dot,
  icon: null,
};
const MA: any = {};

const TxRow = ({
  tx,
  index,
  openId,
  setOpenId,
}: {
  tx: any;
  index: number;
  openId: string | null;
  setOpenId: (v: string | null) => void;
}) => {
  const { action, status_code, processed, meta, created_on } = tx;
  const { data: metaData, error: metaError, updated_by } = meta ?? {};
  const [userName, setUserName] = useState<string | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const s = ACTION_STYLES[action] ?? DEFAULT_STYLE;

  const handleViewUser = useCallback(async () => {
    if (!updated_by || userName) return;
    try {
      setLoadingUser(true);
      const res = await getUserDataByAuth0Id(updated_by);
      if (res?.data?.success && res?.data?.data)
        setUserName(getUserDisplayName(res.data.data));
    } catch {
    } finally {
      setLoadingUser(false);
    }
  }, [updated_by, userName]);

  const hasPayload = !_.isEmpty(metaData);
  const hasError = !_.isEmpty(metaError);

  return (
    <div
      className={`flex gap-3 px-4 py-3.5 transition-colors ${APP_BG.surface} hover:opacity-80`}
    >
      {/* Timeline dot */}
      <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
        <div
          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shadow-sm ${s.cls}`}
        >
          {s.icon}
        </div>
        <div
          className={`flex-1 w-px mt-1.5 min-h-[14px] ${APP_BORDER.dividerBg}`}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5 pb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <PillCell
            value={action}
            config={{
              bg: s.cls
                .split(' ')
                .filter((c) => c.startsWith('bg-'))
                .join(' '),
              text: s.cls
                .split(' ')
                .filter((c) => c.startsWith('text-'))
                .join(' '),
              dot: s.dot,
            }}
            dot
          />
          <PillCell value={status_code ?? 0} preset="statusCode" />
          {processed ? (
            <FiCheckCircle
              className={`h-3.5 w-3.5 flex-shrink-0 ${APP_TEXT.success}`}
            />
          ) : (
            <FiXCircle
              className={`h-3.5 w-3.5 flex-shrink-0 ${APP_TEXT.danger}`}
            />
          )}
          <Text
            as="span"
            size="md"
            color="muted"
            className="ml-auto whitespace-nowrap"
          >
            {getDateTimeAgo(created_on)}
          </Text>
        </div>

        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <Text as="span" size="md" color="muted">
            {getDateTime(created_on)}
          </Text>
          {updated_by && (
            <Text as="span" size="md" color="subtle">
              By:{' '}
              {userName ? (
                <Text as="span" size="md" weight="semibold" color="soft">
                  {userName}
                </Text>
              ) : (
                <AppButton
                  variant="ghost"
                  size="xs"
                  onClick={handleViewUser}
                  disabled={loadingUser}
                  loading={loadingUser}
                  ripple={false}
                >
                  View user
                </AppButton>
              )}
            </Text>
          )}
        </div>

        {(hasPayload || hasError) && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {hasPayload && (
              <Popover
                {...MA}
                open={openId === `p-${index}`}
                handler={() =>
                  setOpenId(openId === `p-${index}` ? null : `p-${index}`)
                }
                placement="bottom-start"
                dismiss={{ outsidePress: true, escapeKey: true }}
              >
                <PopoverHandler>
                  <AppButton variant="neutral" size="xs" ripple={false}>
                    Payload
                  </AppButton>
                </PopoverHandler>
                <PopoverContent
                  {...MA}
                  className={`z-[9999] mt-1 rounded-xl shadow-xl border ${APP_BORDER.default} ${APP_BG.code} max-w-sm`}
                >
                  <pre
                    className={`p-3 max-h-64 overflow-auto font-mono text-xs whitespace-pre-wrap break-words ${APP_TEXT.success}`}
                  >
                    {JSON.stringify(metaData, null, 2)}
                  </pre>
                </PopoverContent>
              </Popover>
            )}
            {hasError && (
              <Popover
                {...MA}
                open={openId === `e-${index}`}
                handler={() =>
                  setOpenId(openId === `e-${index}` ? null : `e-${index}`)
                }
                placement="bottom-start"
                dismiss={{ outsidePress: true, escapeKey: true }}
              >
                <PopoverHandler>
                  <AppButton variant="danger" size="xs" ripple={false}>
                    Error
                  </AppButton>
                </PopoverHandler>
                <PopoverContent
                  {...MA}
                  className={`z-[9999] mt-1 rounded-xl shadow-xl border ${APP_BORDER.default} ${APP_BG.canvas} max-w-sm`}
                >
                  <div className="p-3 max-h-64 overflow-auto flex flex-col gap-1.5">
                    {Object.entries(metaError).map(([k, v]) => (
                      <div
                        key={k}
                        className={`flex justify-between gap-3 text-xs border-b ${APP_BORDER.soft} pb-1 last:border-0`}
                      >
                        <Text
                          as="span"
                          size="base"
                          weight="bold"
                          color="soft"
                          className="whitespace-nowrap"
                        >
                          {k}:
                        </Text>
                        <Text
                          as="span"
                          size="base"
                          color="muted"
                          align="right"
                          className="break-all"
                        >
                          {String(v)}
                        </Text>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const HistoryTab = ({
  transactions,
  loading,
}: {
  transactions: any[];
  loading: boolean;
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  if (loading) {
    return (
      <SectionCard
        testId={tid(TID.EXPANDED_HISTORY)}
        title="Transaction History"
        icon={<MdHistory className="h-3.5 w-3.5" />}
      >
        <div className="p-4 flex flex-col gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div
                className={`w-8 h-8 rounded-full ${APP_BG.code} flex-shrink-0`}
              />
              <div className="flex-1 flex flex-col gap-2 pt-1">
                <div className={`h-3 w-1/3 rounded-lg ${APP_BG.code}`} />
                <div className={`h-3 w-1/2 rounded-lg ${APP_BG.code}`} />
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      testId={tid(TID.EXPANDED_HISTORY)}
      title="Transaction History"
      icon={<MdHistory className="h-3.5 w-3.5" />}
      badge={transactions.length}
    >
      {transactions.length === 0 ? (
        <NoDataPlate
          message={ORGANISM_PROPS.SMART_COOLER_EXPANDED.NO_TRANSACTIONS}
        />
      ) : (
        <div
          className={`divide-y ${APP_BORDER.divideSoft} max-h-[420px] overflow-y-auto scrollbar-thin`}
        >
          {transactions.map((tx, i) => (
            <TxRow
              key={tx.scp_transaction_id ?? i}
              tx={tx}
              index={i}
              openId={openId}
              setOpenId={setOpenId}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
};

/* ─── Main export ────────────────────────────────────────────────────────────── */

export interface SmartCoolerProductsExpandedPanelProps {
  row: ISmartCoolerProduct;
}

export const SmartCoolerProductsExpandedPanel = ({
  row,
}: SmartCoolerProductsExpandedPanelProps) => {
  const { loadingProductById, smartCoolerProduct } = useSelector(
    getSmartCoolerProductsState,
  );
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

  const isMatching =
    (smartCoolerProduct as any)?.data?.smartCoolerProductId ===
    row?.smartCoolerProductId;
  const detail = isMatching ? (smartCoolerProduct as any)?.data : null;
  const transactions: any[] = detail?.transactions ?? [];
  const isLoading = loadingProductById && !isMatching;

  if (isLoading)
    return (
      <SpinnerBlock
        message={ORGANISM_PROPS.SMART_COOLER_EXPANDED.LOADING_MSG}
      />
    );

  return (
    <div data-testid={tid(TID.EXPANDED_PANEL)} className="px-2 py-3 space-y-3">
      {/* Tab switcher */}
      <div className="flex justify-end">
        <SegmentedControl
          testId={tid(TID.EXPANDED_TAB_CONTROL)}
          value={activeTab}
          onChange={setActiveTab}
          size="md"
          shape="pill"
          variant="gradient"
          options={[
            { value: 'details', label: 'Details', icon: FiPackage },
            {
              value: 'history',
              label: `History (${transactions.length})`,
              icon: MdHistory,
            },
          ]}
        />
      </div>

      {activeTab === 'details' ? (
        <DetailsTab row={row} detail={detail} />
      ) : (
        <HistoryTab transactions={transactions} loading={false} />
      )}
    </div>
  );
};
